import { Button, ButtonProps, Tooltip } from '@fluentui/react-components'
import * as React from 'react'
import { forwardRef, ReactElement, useState } from 'react'
import LoadingIcon from '../../../components/Icon/LoadingIcon'
import { RichText } from '../Text/RichText'

export type SubmittingButtonProps = Omit<ButtonProps, 'onClick'> & {
    onClick: () => Promise<any>

    /** Tooltip content; the button keeps its own text, so the tooltip describes rather than labels it. */
    tooltip?: string
    /** Renders the tooltip as markdown through `RichText` instead of as plain text. */
    richTooltip?: boolean

    isSubmitting?: boolean
    setIsSubmitting?: (isSubmitting: boolean) => void
}

/**
 * A Button that shows a spinner while its onClick promise is running, and renders its own tooltip
 * the way {@link TooltippedButton} does. Wrapping it in a Tooltip from the outside does not work:
 * the tooltip has to sit against the Button itself.
 */
const SubmittingButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, SubmittingButtonProps>(function SubmittingButton(
    { tooltip, richTooltip, ...buttonProps },
    ref,
): ReactElement {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const button = (
        <Button
            {...(buttonProps as ButtonProps)}
            ref={ref}
            disabled={isSubmitting || buttonProps.isSubmitting || buttonProps.disabled}
            icon={isSubmitting ? <LoadingIcon /> : buttonProps.icon}
            onClick={async () => {
                try {
                    setIsSubmitting(true)
                    buttonProps.setIsSubmitting?.(true)
                    await buttonProps.onClick()
                } catch (e) {
                    console.error(e)
                    setIsSubmitting(false)
                    buttonProps.setIsSubmitting?.(false)
                } finally {
                    setIsSubmitting(false)
                    buttonProps.setIsSubmitting?.(false)
                }
            }}
        />
    )

    // A tooltip of only whitespace says as little as an empty one, so it counts as absent.
    if (!tooltip?.trim()) {
        return button
    }

    // RichText mounts a tiptap editor, so it is only created once the tooltip has content to render.
    return (
        <Tooltip content={richTooltip ? <RichText content={tooltip} /> : tooltip} relationship={'description'} withArrow>
            {button}
        </Tooltip>
    )
})

export default SubmittingButton
